import { memo, useCallback, useMemo, useState, type FC } from "react";
import { WorkflowList } from "@/components/automations/AutomationList";
import { AutomationsEducationPage } from "@/routes/automations/AutomationsEducationPage";
import IconLock from "central-icons/IconLock";
import IconChainLink1 from "central-icons/IconChainLink1";
import { AutomationsPageHeader } from "@/routes/automations/AutomationsPageHeader";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import {
    AutomationsAccessLevel,
    AutomationsCapabilities,
    useFeatureAutomations,
} from "@/hooks/use-feature-automations";
import { NewAutomationButton } from "@/components/automations/NewAutomationButton";
import { Button } from "@/components/flexkit/Button";
import { Outlet, useMatches, useNavigate } from "react-router-dom";
import type { AutomationTemplate } from "@/utils/automation-templates";
import { PermissionDenied } from "@/components/flexkit/PermissionDenied";
import { AnimatedLoadingContainer } from "@/components/ona/AnimatedLoadingContainer";
import { newWorkflowUrl, webhooksUrl } from "@/utils/automations";
import { useCanCreateAutomation, usePaginatedWorkflows } from "@/queries/automation-queries";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { withGitpodSize } from "@/assets/icons/utils";
import type { RouteHandle } from "@/route-handle";
import { cn } from "@/components/podkit/lib/cn";
import { useListIntegrationDefinitions, useListIntegrations } from "@/queries/integration-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { resolveIntegrationRequirements } from "@/hooks/use-integration-requirements";
import { RequiredIntegrationsDialog } from "@/components/automations/RequiredIntegrationsDialog";

export const AutomationsPage: FC = () => {
    useDocumentTitle("Automations");
    const { accessLevel: workflowAccessLevel, isLoading: isLoadingFlag } = useFeatureAutomations();

    if (isLoadingFlag) {
        return <LoadingState />;
    }

    switch (workflowAccessLevel) {
        case AutomationsAccessLevel.Full:
            return <AutomationsPageContent />;
        case AutomationsAccessLevel.Teaser:
            return <AutomationsEducationPage />;
        case AutomationsAccessLevel.None:
        default:
            return null;
    }
};

const AutomationsPageContent: FC = memo(function AutomationsPageContent() {
    const { workflowIds, isPending: isLoadingWorkflows } = usePaginatedWorkflows({});

    const { data: user } = useAuthenticatedUser();
    const { data: integrationDefinitions } = useListIntegrationDefinitions();
    const { data: integrations } = useListIntegrations(user?.organizationId);

    const { canCreateAutomations, isLoading: isLoadingCreatePermission } = useCanCreateAutomation();
    const { capabilities } = useFeatureAutomations();
    const hasWebhooks = capabilities.has(AutomationsCapabilities.Webhooks);

    // Check if any child route has scrollable: false
    const matches = useMatches();
    const isScrollable = !matches.some(
        (match) => (match.handle as RouteHandle | undefined)?.layout?.scrollable === false,
    );

    // User can view automations if they can create them OR if they have access to any workflows
    // The API will only return workflows you have access to, so an empty response means no workflows
    // have been shared with you.
    const hasAccessToWorkflows = workflowIds && workflowIds.length > 0;
    const canViewAutomations = canCreateAutomations || hasAccessToWorkflows;

    const navigate = useNavigate();

    const [pendingTemplate, setPendingTemplate] = useState<AutomationTemplate | null>(null);

    const handleTemplateSelect = useCallback(
        (template: AutomationTemplate) => {
            const required = template.requiredIntegrations;
            if (!required || required.length === 0) {
                navigate(newWorkflowUrl({ templateId: template.id }));
                return;
            }

            const { allReady } = resolveIntegrationRequirements(required, integrationDefinitions, integrations);
            if (allReady) {
                navigate(newWorkflowUrl({ templateId: template.id }));
            } else {
                setPendingTemplate(template);
            }
        },
        [navigate, integrationDefinitions, integrations],
    );

    const handleManageWebhooks = useCallback(() => {
        navigate(webhooksUrl());
    }, [navigate]);

    const actions = useMemo(() => {
        if (!canCreateAutomations) return undefined;
        return (
            <div className="flex items-center gap-2">
                {hasWebhooks && (
                    <Button
                        variant="outline"
                        LeadingIcon={withGitpodSize(IconChainLink1)}
                        onClick={handleManageWebhooks}
                        data-testid="manage-webhooks-button"
                        data-tracking-id="manage-webhooks-automations-page"
                    >
                        Webhooks
                    </Button>
                )}
                <NewAutomationButton
                    onTemplateSelect={handleTemplateSelect}
                    testId="new-automation-dialog-workflows-page"
                    trackingId="show-new-automation-dialog-automations-page"
                />
            </div>
        );
    }, [canCreateAutomations, hasWebhooks, handleManageWebhooks, handleTemplateSelect]);

    const isLoading = isLoadingCreatePermission || isLoadingWorkflows;

    const content = useMemo(() => {
        if (canViewAutomations) {
            return <WorkflowList onTemplateSelect={handleTemplateSelect} />;
        }
        return (
            <PermissionDenied
                icon={<IconLock size={24} className="text-content-base" />}
                title="You don't have access to Automations"
                description={
                    <>
                        Currently you don&apos;t have permissions to view or manage automations.
                        <br />
                        Please contact your <span className="font-medium">Organization Admin</span> to request access.
                    </>
                }
                data-testid="automations-permission-denied-page"
            />
        );
    }, [canViewAutomations, handleTemplateSelect]);

    return (
        <div
            data-testid="workflows-page"
            className={cn("relative flex h-full grow flex-col gap-6 overflow-x-hidden p-6", {
                "overflow-hidden": !isScrollable,
            })}
        >
            <AutomationsPageHeader actions={isLoading ? undefined : actions} />
            <AnimatedLoadingContainer isLoading={isLoading} message="Preparing automations">
                {content}
            </AnimatedLoadingContainer>
            <Outlet />
            <RequiredIntegrationsDialog
                open={pendingTemplate !== null}
                template={pendingTemplate}
                onClose={() => setPendingTemplate(null)}
            />
        </div>
    );
});
